/**
 * Sticky Add-to-Cart bar (PDP) — SOS-330
 *
 * Visibility: revealed only once the site header has scrolled out of view, and
 * hidden again whenever the header is on screen (top of page, or re-revealed on
 * scroll-up). Implemented by mirroring the theme header's own `shopify-section-header-hidden`
 * class, so the bar reveals at the exact scroll point the header hides.
 *
 * Add to Cart: the bar's dropdown is authoritative. It builds its OWN /cart/add
 * request with the selected variant + quantity + selling_plan (omitted for
 * One-Time), then — without ever navigating away — shows the cart popover if the
 * theme has one (cart-notification / cart-drawer) and otherwise refreshes the
 * header cart bubble. A brief "Added" state on the button confirms the add in case
 * the header (and its cart icon) is scrolled off-screen.
 *
 * Why build the payload explicitly instead of submitting the buy box form: the
 * subscription picker's radios are form-associated to that product form with
 * "Monthly" checked by default, so the form always submits the checked radio's
 * plan regardless of any hidden inputs.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // The custom subscription buy box. If it isn't present, the bar stays hidden.
        this.buyForm = document.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.buyForm) return;

        this.variantInput = this.buyForm.querySelector('input[name="id"], .product-variant-id');

        this.trigger = this.querySelector('.sticky-atc__trigger');
        this.triggerText = this.querySelector('.sticky-atc__trigger-text');
        this.list = this.querySelector('.sticky-atc__list');
        this.options = Array.from(this.querySelectorAll('.sticky-atc__option'));
        this.addButton = this.querySelector('.sticky-atc__add');
        this.addButtonText = this.addButton.querySelector('span');
        this.topButton = this.querySelector('.sticky-atc__top');
        this.selectWrap = this.querySelector('.sticky-atc__select');
        this.selected = null;

        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindListbox();
        this.bindAddToCart();
        this.bindScrollToTop();
        this.bindMainPickerSync();
        this.createObserver();
        this.initSelection();
      }

      /* ---------------- Custom listbox (accessible) ---------------- */

      bindListbox() {
        this.trigger.addEventListener('click', () => this.toggleList());
        this.trigger.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openList();
            this.focusOption(this.selectedIndex() > -1 ? this.selectedIndex() : 0);
          }
        });

        this.options.forEach((opt, i) => {
          opt.setAttribute('tabindex', '-1');
          opt.addEventListener('click', () => this.selectOption(opt, true));
          opt.addEventListener('keydown', (e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                this.focusOption(Math.min(i + 1, this.options.length - 1));
                break;
              case 'ArrowUp':
                e.preventDefault();
                this.focusOption(Math.max(i - 1, 0));
                break;
              case 'Enter':
              case ' ':
                e.preventDefault();
                this.selectOption(opt, true);
                break;
              case 'Escape':
                e.preventDefault();
                this.closeList();
                this.trigger.focus();
                break;
              case 'Tab':
                this.closeList();
                break;
            }
          });
        });

        this.onDocClick = (e) => {
          if (!this.selectWrap.contains(e.target)) this.closeList();
        };
        document.addEventListener('click', this.onDocClick);
      }

      toggleList() {
        this.trigger.getAttribute('aria-expanded') === 'true' ? this.closeList() : this.openList();
      }

      openList() {
        this.list.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.selectWrap.classList.add('is-open');
      }

      closeList() {
        this.list.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.selectWrap.classList.remove('is-open');
      }

      focusOption(index) {
        const opt = this.options[index];
        if (opt) opt.focus();
      }

      selectedIndex() {
        return this.options.findIndex((o) => o === this.selected);
      }

      /* ---------------- Selection ---------------- */

      selectOption(opt, closeAndFocus) {
        this.selected = opt;
        this.options.forEach((o) => o.setAttribute('aria-selected', String(o === opt)));
        this.updateTriggerLabel(opt);

        // Cosmetic: reflect a subscription choice into the main picker radio.
        if (opt.dataset.onetime !== 'true' && opt.dataset.sellingPlan) {
          const radio = document.querySelector(
            `product-subscription-picker input[name="selling_plan"][value="${opt.dataset.sellingPlan}"]`
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (closeAndFocus) {
          this.closeList();
          this.trigger.focus();
        }
      }

      updateTriggerLabel(opt) {
        const label = opt.dataset.label;
        const priceNode = opt.querySelector('.sticky-atc__option-price');
        this.triggerText.innerHTML = '';
        const labelSpan = document.createElement('span');
        labelSpan.className = 'sticky-atc__trigger-label';
        labelSpan.textContent = label;
        this.triggerText.appendChild(labelSpan);
        if (priceNode) {
          const priceClone = priceNode.cloneNode(true);
          priceClone.classList.add('sticky-atc__trigger-price');
          this.triggerText.appendChild(priceClone);
        }
      }

      initSelection() {
        const checked = document.querySelector(
          'product-subscription-picker input[name="selling_plan"]:checked'
        );
        const match = checked
          ? this.options.find((o) => o.dataset.sellingPlan === checked.value && o.dataset.onetime !== 'true')
          : null;
        this.selectOption(match || this.options[0], false);
      }

      /* ---------------- Add to Cart (AJAX, never navigates) ---------------- */

      bindAddToCart() {
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      async addToCart() {
        const variantId = this.variantInput && this.variantInput.value;
        if (!variantId || (this.variantInput && this.variantInput.disabled)) return; // sold out
        if (this.addButton.getAttribute('aria-disabled') === 'true') return;

        const opt = this.selected || this.options[0];
        const isOnetime = opt.dataset.onetime === 'true';
        const qty = parseInt(opt.dataset.quantity || '1', 10) || 1;

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', qty);
        if (!isOnetime && opt.dataset.sellingPlan) {
          formData.append('selling_plan', opt.dataset.sellingPlan);
        }

        // Sections to re-render: the cart popover's (if any) + always the header cart bubble.
        const sectionIds = new Set(['cart-icon-bubble']);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          this.cart.getSectionsToRender().forEach((s) => sectionIds.add(s.id));
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }
        formData.append('sections', Array.from(sectionIds).join(','));
        formData.append('sections_url', window.location.pathname);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        let added = false;
        this.setLoading(true);
        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            // Cart error (e.g. inventory). Surface to console; do not navigate.
            console.error('[sticky-atc] add to cart failed:', data.description || data.message);
            return;
          }

          if (this.cart && typeof this.cart.renderContents === 'function') {
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.cart.renderContents(data); // shows the popover (also refreshes the bubble)
          } else {
            this.refreshCartBubble(data); // page-type cart: no popover, just update the count
          }

          added = true;

          // Let the rest of the theme know the cart changed.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'sticky-add-to-cart',
              productVariantId: variantId,
              cartData: data,
            });
          }
        } catch (e) {
          console.error(e); // network error — stay on the page
        } finally {
          this.setLoading(false);
          if (added) this.flashAdded();
        }
      }

      refreshCartBubble(data) {
        const html = data.sections && data.sections['cart-icon-bubble'];
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('#cart-icon-bubble');
        const current = document.getElementById('cart-icon-bubble');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }

      flashAdded() {
        if (!this.addButtonText) return;
        const original = this.addButton.dataset.defaultText || this.addButtonText.textContent;
        this.addButtonText.textContent = 'Added';
        this.addButton.classList.add('is-added');
        window.clearTimeout(this._addedTimer);
        this._addedTimer = window.setTimeout(() => {
          this.addButtonText.textContent = original;
          this.addButton.classList.remove('is-added');
        }, 1600);
      }

      setLoading(on) {
        this.addButton.classList.toggle('loading', on);
        // Show the spinner while adding; hide it when finished (mirrors product-form.js).
        const spinner = this.addButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
        if (on) this.addButton.setAttribute('aria-disabled', 'true');
        else this.addButton.removeAttribute('aria-disabled');
      }

      /* ---------------- Scroll to top ---------------- */

      bindScrollToTop() {
        if (!this.topButton) return;
        this.topButton.addEventListener('click', () => {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
        });
      }

      /* ---------------- Keep the bar's label in sync when the main picker changes ---------------- */

      bindMainPickerSync() {
        const radios = document.querySelectorAll('product-subscription-picker input[name="selling_plan"]');
        radios.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            const match = this.options.find(
              (o) => o.dataset.sellingPlan === radio.value && o.dataset.onetime !== 'true'
            );
            if (match) {
              this.selected = match;
              this.options.forEach((o) => o.setAttribute('aria-selected', String(o === match)));
              this.updateTriggerLabel(match);
            }
          });
        });
      }

      /* ---------------- Reveal once the header scrolls away ---------------- */

      createObserver() {
        // Reveal at the EXACT scroll point the theme header hides. The theme's
        // StickyHeader toggles `shopify-section-header-hidden` on `.section-header`
        // the instant the header scrolls away (and removes it when the header returns
        // on scroll-up / at the top). Mirroring that class keeps the bar and the header
        // perfectly in sync — the bar is shown only while the header is gone.
        const header = document.querySelector('.section-header');

        if (header) {
          const sync = () =>
            this.setVisible(header.classList.contains('shopify-section-header-hidden'));
          this.headerObserver = new MutationObserver(sync);
          this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
          sync(); // initial state
          return;
        }

        // Fallback only if there's no theme header to track: reveal after a small scroll.
        let ticking = false;
        const update = () => {
          ticking = false;
          this.setVisible(window.scrollY > 80);
        };
        this.onScroll = () => {
          if (!ticking) { ticking = true; requestAnimationFrame(update); }
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        update();
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        if (this.headerObserver) this.headerObserver.disconnect();
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }
    }
  );
}
